import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getRoute } from '../utils/routes';
import visaLogo from '../assets/logo/visa.svg';
import mastercardLogo from '../assets/logo/master-card.svg';
import iyzicoLogo from '../assets/logo/iyzico-colored-bg.svg';

const Footer = ({ audience = 'hiring' }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isTalent = audience === 'talent';
  const currentLanguage = i18n.language || 'tr';

  const handlePricingClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Ana sayfadaysak direkt scroll yap
      const element = document.getElementById('pricing');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Başka bir sayfadaysak önce ana sayfaya git, sonra scroll yap
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById('pricing');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const footerLinks = {
    about: {
      title: t('footer.about'),
      links: [
        { name: t('footer.pricing'), href: '#pricing', isPricing: true },
        { name: t('footer.blog'), href: getRoute('blog', currentLanguage), isRoute: true },
        { name: t('footer.contact'), href: getRoute('contact', currentLanguage), isRoute: true },
        { name: t('footer.faq'), href: getRoute('faq', currentLanguage), isRoute: true },
        { name: t('footer.newsletter'), href: getRoute('newsletter', currentLanguage), isRoute: true },
      ],
    },
    solutions: {
      title: t('footer.solutions'),
      links: isTalent
        ? [
            { name: t('footer.cvAnalysis'), href: '#cv-analysis' },
            { name: t('footer.interviewPrep'), href: '#interview-prep' },
            { name: t('footer.roleGrowth'), href: '#role-growth' },
          ]
        : [
            { name: t('footer.premiumInterview'), href: getRoute('premiumInterview', currentLanguage), isRoute: true },
            { name: t('footer.standardInterview'), href: getRoute('standardInterview', currentLanguage), isRoute: true },
            { name: t('footer.voiceInterview'), href: getRoute('voiceInterview', currentLanguage), isRoute: true },
            { name: t('footer.whatsappInterview'), href: getRoute('whatsappInterview', currentLanguage), isRoute: true },
            { name: t('footer.phoneInterview'), href: getRoute('phoneInterview', currentLanguage), isRoute: true },
            { name: t('footer.interviewObserver'), href: getRoute('interviewObserver', currentLanguage), isRoute: true },
            { name: t('footer.textInterview'), href: getRoute('textInterview', currentLanguage), isRoute: true },
            { name: t('footer.peerInterview'), href: getRoute('peerInterview', currentLanguage), isRoute: true },
            { name: t('footer.cvScoring'), href: getRoute('cvScoring', currentLanguage), isRoute: true },
          ],
    },
    legal: {
      title: t('footer.legal'),
      links: [
        { name: t('footer.terms'), href: getRoute('terms', currentLanguage), isRoute: true },
        { name: t('footer.privacy'), href: getRoute('privacy', currentLanguage), isRoute: true },
        { name: t('footer.kvkk'), href: getRoute('kvkk', currentLanguage), isRoute: true },
        { name: t('footer.cookies'), href: getRoute('cookies', currentLanguage), isRoute: true },
      ],
    },
  };

  return (
    <footer className="bg-primary pt-12 sm:pt-16 pb-6 sm:pb-8">
      <div className="container-custom px-4 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-12">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="mb-4 sm:mb-6 leading-tight">
              <div className="text-xl sm:text-2xl font-bold text-white">
                Hire<span className="text-accent-teal">Think</span>
              </div>
              <div className="text-xs sm:text-sm text-white mt-1">
                Yapay Zeka Teknolojileri A.Ş.
              </div>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-white/80">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a href="mailto:info@hirethink.ai" className="footer-link text-sm sm:text-base break-all flex-1 min-w-0">
                  info@hirethink.ai
                </a>
              </div>
              <a
                className="flex items-center gap-2 text-white/80 hover:opacity-90 transition-opacity touch-manipulation"
                href="tel:08503078061"
              >
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span className="footer-link text-sm sm:text-base">0850 307 80 61</span>
              </a>
              <div className="flex items-start gap-2 text-white/80">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <a 
                  href="https://maps.google.com/?q=Bilişim+Vadisi+Ayazağa+Vadi+Istanbul+Park+Etabı+Kemerburgaz+Caddesi+D:7A+Blok+Sarıyer+İstanbul" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="footer-link text-sm sm:text-base"
                >
                  {t('footer.address')}
                </a>
              </div>
            </div>
            
            <p className="text-white/75 text-xs sm:text-sm leading-relaxed max-w-sm mb-4">
              {t('footer.description')}
            </p>

            <div className="text-white/75 text-xs mb-4 space-y-1">
              <p>
                <strong>Vergi No:</strong> 4621090464
              </p>
              <p>
                <strong>Vergi Dairesi:</strong> Maslak
              </p>
            </div>

            {/* Badges */}
            <div className="mt-4 sm:mt-6 flex flex-wrap items-center gap-2 sm:gap-3">
              {/* KVKK Badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-accent-teal rounded flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <span className="text-white text-xs sm:text-sm font-medium">{t('footer.kvkkCompliant')}</span>
              </div>
              {/* ISO 27001 Badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-accent-teal rounded flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <span className="text-white text-xs sm:text-sm font-medium">{t('footer.iso27001')}</span>
              </div>
            </div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([key, section]) => (
            <div key={key}>
              <h3 className="text-white font-subheading mb-3 sm:mb-4 text-sm sm:text-base">{section.title}</h3>
              <ul className="space-y-2 sm:space-y-3">
                {section.links.map((link) => (
                  <li key={link.name}>
                    {link.isPricing ? (
                      <a href={link.href} onClick={handlePricingClick} className="footer-link text-xs sm:text-sm touch-manipulation">
                        {link.name}
                      </a>
                    ) : link.isRoute ? (
                      <Link to={link.href} className="footer-link text-xs sm:text-sm touch-manipulation">
                        {link.name}
                      </Link>
                    ) : (
                      <a href={link.href} className="footer-link text-xs sm:text-sm touch-manipulation">
                        {link.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-6 sm:pt-8 flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
          <div className="flex flex-col gap-3 sm:gap-4 w-full md:w-auto items-center md:items-start">
            <p className="text-white/75 text-xs sm:text-sm text-center md:text-left">
              © 2025 HireThink Yapay Zeka Teknolojileri A.Ş. Tüm hakları saklıdır.
            </p>
            {/* Payment Logos */}
            <div className="flex items-center gap-2 sm:gap-3">
              <img
                src={visaLogo}
                alt="Visa"
                width="500"
                height="160"
                className="h-5 sm:h-6 w-auto opacity-80 hover:opacity-100 transition-opacity brightness-0 invert"
                loading="lazy"
                decoding="async"
              />
              <img
                src={mastercardLogo}
                alt="Mastercard"
                className="h-5 sm:h-6 opacity-80 hover:opacity-100 transition-opacity"
                width="40"
                height="24"
                loading="lazy"
                decoding="async"
              />
              <img
                src={iyzicoLogo}
                alt="iyzico"
                className="h-5 sm:h-6 opacity-80 hover:opacity-100 transition-opacity"
                width="108"
                height="38"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          
          {/* Social Links */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 md:gap-4 flex-wrap w-full md:w-auto">
            <a 
              href="https://www.youtube.com/@hirethink" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors touch-manipulation"
              aria-label="YouTube"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span className="text-xs sm:text-sm font-medium hidden sm:inline">{t('footer.youtube')}</span>
            </a>
            <a 
              href="https://www.linkedin.com/company/hirethink" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors touch-manipulation"
              aria-label="LinkedIn"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
              <span className="text-xs sm:text-sm font-medium hidden sm:inline">{t('footer.linkedin')}</span>
            </a>
            <a 
              href="mailto:info@hirethink.ai" 
              className="flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors touch-manipulation"
              aria-label="Email"
            >
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium hidden sm:inline">{t('footer.emailLabel')}</span>
            </a>
            <a 
              href="tel:08503078061"
              className="flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors touch-manipulation"
              aria-label="Phone"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium hidden sm:inline">0850 307 80 61</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
